import BaseProduct from './BaseProduct';
import { ProductId } from '../types';
import store, { getLayerOpacity } from '../state';
import ManifestTimelineHelper from '../services/ManifestTimelineHelper';
import maplibregl from 'maplibre-gl';
import { cleanUrl, authenticatedFetch, getApiServer } from '../config';

export class FreezingLevelLayer extends BaseProduct {
  public readonly id: ProductId = 'freezing_level';
  private geojsonSourceId = 'freezing-level-contours-source';
  private contourLayerId = 'freezing-level-contour-line';
  private labelLayerId = 'freezing-level-contour-label';
  private rasterBaseId = 'freezing_level_fill';
  private lastModel: string = '';

  protected onActivate(): void {
    this.refresh();
  }

  protected onDeactivate(): void {
    this.removeContourLayers();
    this.layerManager.removeBufferedRasterLayer(this.rasterBaseId);
    this.lastModel = '';
  }

  protected onTimeUpdate(_date: string, _hour: string): void {
    this.refresh();
  }

  public setFillVisibility(enabled: boolean): void {
    const opacity = enabled ? getLayerOpacity(this.id, 1.0) : 0;
    this.layerManager.setLayerOpacity(this.rasterBaseId, opacity);
  }

  public renderInterpolated(targetTimeMs: number): void {
    if (!this.active) return;
    const state = store.getState();
    const manifest = state.manifest;
    if (!manifest) return;

    const rawModel = state.selectedModels[this.id];
    let model = (rawModel && rawModel !== 'blend') ? rawModel : 'hrrr';
    if (ManifestTimelineHelper.getProductFrames(manifest, 'freezing_level', model).length === 0) {
      const avail = ManifestTimelineHelper.getAvailableModelsForProduct(manifest, 'freezing_level');
      for (const mObj of avail) {
        if (ManifestTimelineHelper.getProductFrames(manifest, 'freezing_level', mObj.id).length > 0) {
          model = mObj.id;
          break;
        }
      }
    }

    const fillEnabled = state.freezingLevelFillEnabled === true;
    const rasterOpacity = fillEnabled ? getLayerOpacity(this.id, 1.0) : 0;
    this.updateStandardInterpolatedRaster({
      layerId: this.rasterBaseId,
      product: this.id,
      model,
      targetTime: targetTimeMs,
      opacity: rasterOpacity,
      beforeId: this.contourLayerId,
      minzoom: 0,
      maxzoom: 5
    });
  }

  private removeContourLayers() {
    if (this.map.getLayer(this.labelLayerId)) this.map.removeLayer(this.labelLayerId);
    if (this.map.getLayer(this.contourLayerId)) this.map.removeLayer(this.contourLayerId);
    if (this.map.getSource(this.geojsonSourceId)) this.map.removeSource(this.geojsonSourceId);
  }

  public async refresh() {
    if (!this.active) return;
    const state = store.getState();
    const manifest = state.manifest;
    const opacity = getLayerOpacity(this.id, 1.0);
    const current = state.currentTimeStep;
    if (!current) return;

    const rawModel = state.selectedModels[this.id];
    let model = (rawModel && rawModel !== 'blend') ? rawModel : 'hrrr';
    let clampedFrame = manifest ? ManifestTimelineHelper.getClampedFrame(manifest, 'freezing_level', model, current) : null;

    if (!clampedFrame && manifest) {
      const avail = ManifestTimelineHelper.getAvailableModelsForProduct(manifest, 'freezing_level');
      for (const mObj of avail) {
        const frame = ManifestTimelineHelper.getClampedFrame(manifest, 'freezing_level', mObj.id, current);
        if (frame) {
          model = mObj.id;
          clampedFrame = frame;
          break;
        }
      }
    }

    if (!clampedFrame) {
      this.removeContourLayers();
      this.layerManager.removeBufferedRasterLayer(this.rasterBaseId);
      return;
    }

    if (this.lastModel && this.lastModel !== model) {
      this.layerManager.removeBufferedRasterLayer(this.rasterBaseId);
    }
    this.lastModel = model;

    const dateStr = clampedFrame.dateStr;
    const hourStr = clampedFrame.hourStr.replace(/^Z/, '');

    // 1. Update background raster gradient positioned below contour lines
    if (manifest) {
      const fillEnabled = state.freezingLevelFillEnabled === true;
      const rasterOpacity = fillEnabled ? getLayerOpacity(this.id, 1.0) : 0;
      this.updateStandardInterpolatedRaster({
        layerId: this.rasterBaseId,
        product: this.id,
        model,
        targetTime: current,
        opacity: rasterOpacity,
        beforeId: this.contourLayerId,
        minzoom: 0,
        maxzoom: 5
      });
    }

    // 2. Fetch and render vector contour lines and labels
    const apiServer = getApiServer(manifest);
    const tileRoot = (manifest?.tile_base || 'tiles').replace(/^\/+|\/+$/g, '');
    const cleanServer = apiServer.replace(/\/$/, '');

    const primaryUrl = cleanUrl(`${cleanServer}/${tileRoot}/forecast/freezing_level/freezing_level_${model}_${dateStr}_Z${hourStr}_contours.geojson`);
    const fallbackUrl = cleanUrl(`${cleanServer}/${tileRoot}/forecast/freezing_level/gw_freezing_level_${model}_${dateStr}_Z${hourStr}_contours.geojson`);

    try {
      let res = await authenticatedFetch(primaryUrl);
      if (!res.ok) {
        res = await authenticatedFetch(fallbackUrl);
      }
      if (res.ok) {
        const geojson = await res.json();
        const source = this.map.getSource(this.geojsonSourceId) as maplibregl.GeoJSONSource;
        if (source) {
          source.setData(geojson);
        } else {
          this.map.addSource(this.geojsonSourceId, { type: 'geojson', data: geojson });

          const colorInterpolation: maplibregl.ExpressionSpecification = [
            'interpolate',
            ['linear'],
            ['get', 'elevation_ft'],
            0,     '#8b5cf6', // 0 ft (purple/violet)
            3000,  '#3b82f6', // 3,000 ft (blue)
            6000,  '#06b6d4', // 6,000 ft (cyan)
            9000,  '#10b981', // 9,000 ft (emerald green)
            12000, '#eab308', // 12,000 ft (yellow/gold)
            15000, '#f97316', // 15,000 ft (orange)
            18000, '#ef4444', // 18,000 ft (red)
            22000, '#b91c1c'  // 22,000 ft+ (deep crimson)
          ];

          this.map.addLayer({
            id: this.contourLayerId,
            type: 'line',
            source: this.geojsonSourceId,
            paint: {
              'line-color': colorInterpolation,
              'line-width': [
                'interpolate',
                ['linear'],
                ['zoom'],
                4, 1.8,
                8, 2.8,
                12, 3.8
              ],
              'line-opacity': 0.9 * opacity
            }
          });

          this.map.addLayer({
            id: this.labelLayerId,
            type: 'symbol',
            source: this.geojsonSourceId,
            layout: {
              'text-field': ['concat', ['to-string', ['get', 'elevation_ft']], ' ft'],
              'text-font': ['Noto Sans Regular'],
              'text-size': 11,
              'symbol-placement': 'line'
            },
            paint: {
              'text-color': colorInterpolation,
              'text-halo-color': '#000000',
              'text-halo-width': 2.0
            }
          });
        }
      } else {
        this.removeContourLayers();
      }
    } catch (_e) {
      this.removeContourLayers();
    }
  }
}

export default FreezingLevelLayer;
